import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem, CartTotals, Product, CouponCode } from '../shared/types';
import { getEftDiscount } from '../shared/lib/businessRules/paymentRules';
import { useSettingsStore } from './settingsStore';

interface CartState {
  items: CartItem[];
  coupon: CouponCode | null;
  shippingRuleId: string;
  paymentMethod: string;

  // Actions
  addItem: (product: Product, quantity?: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  setCoupon: (coupon: CouponCode | null) => void;
  setShippingRuleId: (id: string) => void;
  setPaymentMethod: (method: string) => void;

  // Derived
  getTotals: () => CartTotals;
  getItemCount: () => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      coupon: null,
      shippingRuleId: 'izmir-free',
      paymentMethod: 'credit_card',

      addItem: (product, quantity = 1) => {
        set((state) => {
          const existing = state.items.find((i) => i.product.id === product.id);
          if (existing) {
            return {
              items: state.items.map((i) =>
                i.product.id === product.id
                  ? { ...i, quantity: i.quantity + quantity }
                  : i
              ),
            };
          }
          return {
            items: [...state.items, { product, quantity, addedAt: Date.now() }],
          };
        });
      },

      removeItem: (productId) => {
        set((state) => ({
          items: state.items.filter((i) => i.product.id !== productId),
        }));
      },

      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(productId);
          return;
        }
        set((state) => ({
          items: state.items.map((i) =>
            i.product.id === productId ? { ...i, quantity } : i
          ),
        }));
      },

      clearCart: () => set({ items: [], coupon: null }),

      setCoupon: (coupon) => set({ coupon }),

      setShippingRuleId: (id) => set({ shippingRuleId: id }),

      setPaymentMethod: (method) => set({ paymentMethod: method }),

      getTotals: (): CartTotals => {
        const { items, coupon, paymentMethod } = get();
        const settings = useSettingsStore.getState();

        const subtotal = items.reduce(
          (sum, item) => sum + item.product.price * item.quantity,
          0
        );

        // Product discounts
        const productDiscount = items.reduce((sum, item) => {
          if (item.product.oldPrice) {
            return sum + (item.product.oldPrice - item.product.price) * item.quantity;
          }
          return sum;
        }, 0);

        // Coupon discount
        let couponDiscount = 0;
        if (coupon?.isValid) {
          if (coupon.discountType === 'percent') {
            couponDiscount = Math.round(subtotal * (coupon.discountValue / 100) * 100) / 100;
          } else {
            couponDiscount = Math.min(coupon.discountValue, subtotal);
          }
        }

        // EFT Discount
        const eftDiscount = getEftDiscount(subtotal - couponDiscount, paymentMethod as any, {
          minThresholdTL: settings.paymentRules.minThresholdTL,
          eftDiscountPercent: settings.paymentRules.eftDiscountPercent,
          hideCreditCardAboveThreshold: settings.paymentRules.hideCreditCardAboveThreshold,
        });

        // Shipping
        const subtotalAfterDiscounts = subtotal - couponDiscount - eftDiscount;
        const shippingCost = items.length === 0
          ? 0
          : subtotalAfterDiscounts >= 10000
          ? 0
          : 500;

        const total = Math.max(0, subtotalAfterDiscounts + shippingCost);
        const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

        return {
          subtotal,
          discount: productDiscount,
          shippingCost,
          couponDiscount,
          eftDiscount,
          total,
          itemCount,
        };
      },

      getItemCount: () => {
        return get().items.reduce((sum, i) => sum + i.quantity, 0);
      },
    }),
    {
      name: 'ar-klima-cart',
    }
  )
);
